import { Input } from 'antd';
import { BaseButton } from '../../../../components/UI/BaseButton';
import { SpaceVisibilityCard } from '../../../../components/Cards/SpaceVisibilityCard';
import { WORKSPACE_VISIBILITY } from '../../../Onboarding/WorkspaceOnboarding';
import Workspace from '../../../../types/Workspace';
import { useEffect, useState } from 'react';
import CustomTooltip from '../../../../components/UI/Tooltip/CustomTooltip';

export type SpaceSettingsGeneralProps = {
  space?: Workspace;
  onSave: (spaceName: string, visibility: WORKSPACE_VISIBILITY) => void;
  setIsChanged?: (isChanged: boolean) => void;
  isDisabled?: boolean;
  rerender?: boolean;
  setRerender?: (rerender: boolean) => void;
};

export const SpaceSettingsGeneral = ({
  space,
  onSave,
  setIsChanged,
  isDisabled,
  rerender,
  setRerender,
}: SpaceSettingsGeneralProps) => {
  const [spaceName, setSpaceName] = useState<string>(space?.name || '');
  const [selectedVisibility, setSelectedVisibility] = useState<WORKSPACE_VISIBILITY>(
    space?.visibility === WORKSPACE_VISIBILITY.Private ? WORKSPACE_VISIBILITY.Private : WORKSPACE_VISIBILITY.Public
  );
  const isChanged = (space?.name !== spaceName || space?.visibility !== selectedVisibility) && spaceName.trim() !== '';

  const handleUpdateSpaceName = (name: string) => {
    setSpaceName(name);
    setIsChanged &&
      setIsChanged((name !== space?.name || selectedVisibility !== space?.visibility) && name.trim() !== '');
  };

  const handleSpaceNameBlur = () => {
    // If field is empty, reset to last saved value
    if (!spaceName.trim()) {
      setSpaceName(space?.name || '');
      setIsChanged && setIsChanged(selectedVisibility !== space?.visibility);
    }
  };

  const handleVisibilityChange = (visibility: WORKSPACE_VISIBILITY) => {
    setSelectedVisibility(visibility);
    setIsChanged &&
      setIsChanged((space?.name !== spaceName || visibility !== space?.visibility) && spaceName.trim() !== '');
  };

  useEffect(() => {
    if (rerender) {
      setSpaceName(space?.name || '');
      setSelectedVisibility(
        space?.visibility === WORKSPACE_VISIBILITY.Private ? WORKSPACE_VISIBILITY.Private : WORKSPACE_VISIBILITY.Public
      );
      setRerender && setRerender(false);
    }
  }, [rerender]);

  return (
    <div className="w-full h-full px-8 flex flex-col items-start justify-between">
      <div className="w-full flex flex-col items-start gap-2 mt-6">
        <div className="text-brand-grey-dark text-base font-semibold">Teamspace Settings</div>

        <div className="w-full flex flex-col items-start gap-2 mt-4">
          <div className="text-brand-grey-dark text-xs font-semibold leading-none">Teamspace name</div>
          <Input
            value={spaceName}
            type="text"
            placeholder="Enter teamspace name"
            className="w-full"
            onChange={(e) => handleUpdateSpaceName(e.target.value)}
            onBlur={handleSpaceNameBlur}
          />
        </div>

        <div className="text-brand-grey-dark text-xs font-semibold leading-none mt-6">Teamspace visibility</div>
        <div className="flex gap-2">
          <SpaceVisibilityCard
            visibility={WORKSPACE_VISIBILITY.Public}
            selected={selectedVisibility === WORKSPACE_VISIBILITY.Public}
            current={space?.visibility === WORKSPACE_VISIBILITY.Public}
            onSelected={() => handleVisibilityChange(WORKSPACE_VISIBILITY.Public)}
          />
          <SpaceVisibilityCard
            visibility={WORKSPACE_VISIBILITY.Private}
            selected={selectedVisibility === WORKSPACE_VISIBILITY.Private}
            current={space?.visibility === WORKSPACE_VISIBILITY.Private}
            onSelected={() => handleVisibilityChange(WORKSPACE_VISIBILITY.Private)}
          />
        </div>
      </div>

      {isDisabled && (
        <CustomTooltip title={'You cannot change the teamspace settings'}>
          <div className="absolute top-[80px] left-[250px] w-[calc(100%-250px)] h-[calc(100%-80px)] bg-white/30 z-10 cursor-not-allowed pointer-events-auto" />
        </CustomTooltip>
      )}

      <div className="w-full flex justify-end items-center mb-3">
        <BaseButton
          type="primary"
          size="medium"
          disabled={!isChanged}
          onClick={() => onSave(spaceName, selectedVisibility)}
        >
          Save Changes
        </BaseButton>
      </div>
    </div>
  );
};
